import { Entity } from './base-raptor-models';
import { User } from './user.model';
import { InspectionSection } from './inspection.model';
import type { QuoteSection } from './quote.model';

/**
 * Tracks the lifecycle of a service work assignment.
 * Pending -> InProgress -> Completed or Cancelled.
 */
export enum JobStatus {
    Pending = 'Pending',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

/**
 * Determines how labor is billed for a Job.
 * FlatRate uses a fixed price regardless of actual time spent.
 * TimeAndMaterials bills based on actual hours worked plus parts consumed.
 */
export enum JobPricingModel {
    FlatRate = 'FlatRate',
    TimeAndMaterials = 'TimeAndMaterials',
}

/**
 * Classification of technician expertise required for a Job.
 * Used for assignment matching and labor rate determination.
 */
export enum SkillLevel {
    EntryLevel = 'EntryLevel',
    Standard = 'Standard',
    Master = 'Master',
    Specialty = 'Specialty',
}

/**
 * Tenant-scoped classification for grouping related Jobs.
 * Optionally mapped to VMRS system codes for industry-standard categorization.
 *
 * Extends Entity so the admin management views can read and display the audit
 * fields returned by the JobCategory API; the list is tenant-global so branchId
 * is unused even though it is inherited as optional.
 */
export interface JobCategory extends Entity {
    name: string;
    code: string;
    description?: string;
    vmrsSystemCode?: string;
    sortOrder: number;
    defaultInspectionTemplateId?: string;
}

export function createMockJobCategory(overrides: Partial<JobCategory> = {}): JobCategory {
    const defaultJobCategory: JobCategory = {
        id: 'mock-job-category-id',
        name: 'mock-job-category-name',
        code: 'mock-job-category-code',
        description: 'mock-job-category-description',
        vmrsSystemCode: 'mock-job-category-vmrs',
        sortOrder: 0,
        defaultInspectionTemplateId: 'mock-default-template-id',
    };

    return {
        ...defaultJobCategory,
        ...overrides,
    };
}

/**
 * Represents a discrete unit of service work within a WorkOrder.
 * Each Job maps to one InspectionSection (the diagnostic source) and is applied
 * to a Quote/WorkOrder Section whose Items carry the Package composition.
 */
export interface Job extends Entity {
    /** Display name for this Job shown in the user interface */
    name: string;

    /** Optional external Job code or identifier */
    code?: string;

    /** Optional description of the work to be performed */
    description?: string;

    /** Foreign key to the JobCategory for classification */
    categoryId?: string;

    /** Navigation property to the JobCategory */
    category?: JobCategory;

    /** Current lifecycle status of this Job */
    status: JobStatus;

    /** Foreign key to the User assigned to perform this Job */
    assignedToUserId?: string;

    /** Navigation property to the assigned technician */
    assignedTo?: User;

    /** Foreign key to the InspectionSection that originated this Job */
    inspectionSectionId?: string;

    /** Navigation property to the originating InspectionSection */
    inspectionSection?: InspectionSection;

    /** Estimated hours from labor guide or manual entry */
    estimatedHours?: number;

    /** Source of the labor estimate (e.g., "Mitchell", "AllData", "Manual") */
    laborGuideSource?: string;

    /** Determines how labor is billed: flat rate vs. time and materials */
    pricingModel: JobPricingModel;

    /** Required technician skill level for this Job */
    skillLevel?: SkillLevel;

    /** VMRS code for industry-standard service classification */
    vmrsCode?: string;

    /** VMRS Work Accomplished code for reporting */
    vmrsWorkAccomplishedCode?: string;

    /** Whether this Job is active and visible in the system */
    isActive: boolean;

    /** Collection of QuoteSections linked to this Job via transfer */
    quoteSections?: QuoteSection[];
}
